// src/lib/utils/date/userTimezone.ts

/**
 * Lecture du fuseau IANA de l'appareil, fournie par l'app à `<MailvistaSDK>` (F-1.19). Le mobile lit
 * celui du système avec `expo-localization` ; le web n'en fournit pas : le navigateur le donne par
 * `Intl`.
 */
export type UserTimezoneReader = () => string | null | undefined;

/** Ce que rend ICU quand il ne connaît pas le fuseau de l'appareil. */
const UNKNOWN_TIMEZONE = "Etc/Unknown";

/**
 * Fuseau IANA de l'utilisateur (ex. « Europe/Paris »), envoyé à l'assistant en `userTimezone`
 * (F-1.19) : celui que l'app fournit, sinon celui de `Intl`.
 *
 * `undefined` s'il est inconnu, jamais `null` ni chaîne vide : le champ est alors omis, et l'Api-Ai
 * prend Paris par défaut (un `null` lui faisait répondre 500). Lu à chaque appel : un utilisateur
 * qui change de fuseau en voyage l'envoie sans redémarrer l'app.
 */
export function getUserTimezone(
    readFromApp?: UserTimezoneReader,
): string | undefined {
    return (
        readTimezone(readFromApp) ??
        readTimezone(() => Intl.DateTimeFormat().resolvedOptions().timeZone)
    );
}

/** Une lecture qui échoue, vide ou inconnue donne `undefined`. */
const readTimezone = (read?: UserTimezoneReader): string | undefined => {
    try {
        const timezone = read?.()?.trim();
        return timezone && timezone !== UNKNOWN_TIMEZONE ? timezone : undefined;
    } catch {
        return undefined;
    }
};
